import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import {
  Button,
  Card,
  Checkbox,
  ErrorNote,
  Field,
  Input,
  PageTitle,
  SaveState,
  Spinner,
  Textarea,
} from '../components/ui.jsx';

const SOCIAL_KEYS = ['instagram', 'tiktok', 'linkedin', 'facebook', 'behance', 'x'];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);

  function load() {
    setError(null);
    api
      .get('/api/admin/settings')
      .then((data) => setSettings(data.settings))
      .catch((err) => setError(err.message));
  }

  useEffect(load, []);

  function set(key, value) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  function setNested(group, key, value) {
    setSettings((prev) => ({ ...prev, [group]: { ...(prev[group] ?? {}), [key]: value } }));
  }

  async function save() {
    setState('saving');
    setError(null);
    try {
      await api.patch('/api/admin/settings', {
        siteName: settings.siteName,
        tagline: settings.tagline,
        description: settings.description,
        logoUrl: settings.logoUrl || null,
        faviconUrl: settings.faviconUrl || null,
        defaultOgImage: settings.defaultOgImage || null,
        contact: settings.contact ?? {},
        socials: settings.socials ?? {},
        featureFlags: settings.featureFlags ?? {},
        analyticsEnabled: settings.analyticsEnabled,
        heatmapEnabled: settings.heatmapEnabled,
        cookieBannerEnabled: settings.cookieBannerEnabled,
        maintenanceMode: settings.maintenanceMode,
      });
      setState('saved');
      setTimeout(() => setState('idle'), 2500);
    } catch (err) {
      setState('error');
      setError(err.message);
    }
  }

  if (error && !settings) return <ErrorNote error={error} onRetry={load} />;
  if (!settings) return <Spinner />;

  const contact = settings.contact ?? {};
  const socials = settings.socials ?? {};

  return (
    <>
      <PageTitle
        title="Settings"
        description="Site identity, contact details, and feature toggles."
        action={
          <div className="flex items-center gap-4">
            <SaveState state={state} />
            <Button onClick={save} disabled={state === 'saving'}>
              Save settings
            </Button>
          </div>
        }
      />

      <ErrorNote error={error} />

      <div className="grid gap-8 lg:grid-cols-2">
        <Card className="p-8">
          <h2 className="text-xl">Identity</h2>
          <div className="mt-6 space-y-5">
            <Field label="Site name" htmlFor="siteName">
              <Input
                id="siteName"
                value={settings.siteName ?? ''}
                onChange={(e) => set('siteName', e.target.value)}
              />
            </Field>
            <Field label="Tagline" htmlFor="tagline">
              <Input
                id="tagline"
                value={settings.tagline ?? ''}
                onChange={(e) => set('tagline', e.target.value)}
              />
            </Field>
            <Field
              label="Description"
              htmlFor="description"
              hint="Used as the default meta description."
            >
              <Textarea
                id="description"
                rows={4}
                value={settings.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
            <Field
              label="Default social image"
              htmlFor="ogImage"
              hint="Shown when a page is shared and has no image of its own."
            >
              <Input
                id="ogImage"
                value={settings.defaultOgImage ?? ''}
                onChange={(e) => set('defaultOgImage', e.target.value)}
                placeholder="https://…"
              />
            </Field>
          </div>
        </Card>

        <div className="space-y-8">
          <Card className="p-8">
            <h2 className="text-xl">Contact</h2>
            <div className="mt-6 space-y-5">
              <Field label="Email" htmlFor="email">
                <Input
                  id="email"
                  type="email"
                  value={contact.email ?? ''}
                  onChange={(e) => setNested('contact', 'email', e.target.value)}
                />
              </Field>
              <Field label="Phone" htmlFor="phone">
                <Input
                  id="phone"
                  value={contact.phone ?? ''}
                  onChange={(e) => setNested('contact', 'phone', e.target.value)}
                />
              </Field>
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-xl">Social</h2>
            <p className="mt-2 text-sm text-muted">
              Empty fields are hidden from the site footer rather than shown as blanks.
            </p>
            <div className="mt-6 space-y-4">
              {SOCIAL_KEYS.map((key) => (
                <Field key={key} label={key} htmlFor={`social-${key}`}>
                  <Input
                    id={`social-${key}`}
                    value={socials[key] ?? ''}
                    onChange={(e) => setNested('socials', key, e.target.value)}
                    placeholder="https://…"
                  />
                </Field>
              ))}
            </div>
          </Card>

          <Card className="p-8">
            <h2 className="text-xl">Toggles</h2>
            <div className="mt-6 space-y-4">
              <Checkbox
                label="Analytics enabled"
                checked={Boolean(settings.analyticsEnabled)}
                onChange={(e) => set('analyticsEnabled', e.target.checked)}
              />
              <Checkbox
                label="Heatmap capture enabled"
                checked={Boolean(settings.heatmapEnabled)}
                onChange={(e) => set('heatmapEnabled', e.target.checked)}
              />
              <Checkbox
                label="Cookie consent banner"
                checked={Boolean(settings.cookieBannerEnabled)}
                onChange={(e) => set('cookieBannerEnabled', e.target.checked)}
              />
              <Checkbox
                label="Maintenance mode"
                checked={Boolean(settings.maintenanceMode)}
                onChange={(e) => set('maintenanceMode', e.target.checked)}
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
